// Runtime configuration, fetched from /config.json at startup (the nginx
// container renders it from environment variables), so one build serves
// every environment without rebuilding.

export interface AppConfig {
  appName: string;
  /** Prefix for REST calls; "" means same origin (the supported deploy). */
  apiBaseUrl: string;
  /** Prefix for the gRPC-Web panel streams; "" means same origin. */
  panelBaseUrl: string;
  /** Path (under apiBaseUrl) that starts the Discord OAuth2 login. */
  discordLoginPath: string;
  /** Show the "dev token" login (only works when Master runs DEV_MODE). */
  devLogin: boolean;
  /** Seconds of live traffic history kept in memory for charts. */
  trafficWindowSec: number;
  /** Background refetch interval for REST lists, in ms (0 = off). */
  refetchIntervalMs: number;
  /**
   * Cloudflare Turnstile site key for the login page (ADR-0049); "" = no
   * widget. Master checks the token only when TURNSTILE_SECRET_KEY is set.
   */
  turnstileSiteKey: string;
}

export const defaultConfig: AppConfig = {
  appName: "OneMCFirewall",
  apiBaseUrl: "",
  panelBaseUrl: "",
  discordLoginPath: "/api/v1/auth/discord/login",
  devLogin: false,
  trafficWindowSec: 300,
  refetchIntervalMs: 15000,
  turnstileSiteKey: "",
};

function trimSlash(s: string): string {
  return s.replace(/\/+$/, "");
}

/** Merges a parsed config.json over the defaults, ignoring wrong types. */
export function normalizeConfig(raw: unknown): AppConfig {
  const c: AppConfig = { ...defaultConfig };
  if (!raw || typeof raw !== "object") return c;
  const r = raw as Record<string, unknown>;
  if (typeof r.appName === "string" && r.appName.trim()) c.appName = r.appName.trim();
  if (typeof r.apiBaseUrl === "string") c.apiBaseUrl = trimSlash(r.apiBaseUrl.trim());
  if (typeof r.panelBaseUrl === "string") c.panelBaseUrl = trimSlash(r.panelBaseUrl.trim());
  if (typeof r.discordLoginPath === "string" && r.discordLoginPath.startsWith("/")) c.discordLoginPath = r.discordLoginPath;
  if (typeof r.devLogin === "boolean") c.devLogin = r.devLogin;
  if (typeof r.trafficWindowSec === "number" && r.trafficWindowSec >= 30 && r.trafficWindowSec <= 3600) {
    c.trafficWindowSec = Math.round(r.trafficWindowSec);
  }
  if (typeof r.refetchIntervalMs === "number" && r.refetchIntervalMs >= 0) c.refetchIntervalMs = r.refetchIntervalMs;
  if (typeof r.turnstileSiteKey === "string") c.turnstileSiteKey = r.turnstileSiteKey.trim();
  return c;
}

let current: AppConfig = defaultConfig;

export async function loadConfig(): Promise<AppConfig> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (res.ok) current = normalizeConfig(await res.json());
  } catch {
    // Missing/invalid config.json: run on defaults (same-origin API).
  }
  return current;
}

export function getConfig(): AppConfig {
  return current;
}

export function setConfigForTests(c: Partial<AppConfig>): void {
  current = { ...defaultConfig, ...c };
}
